import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, DOCUMENT, signal } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import { Subject, interval, merge } from 'rxjs';
import { startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { User } from './models/user';
import { WebFooterComponent } from './modules/website/components/web-footer/web-footer';
import { WebNavComponent } from './modules/website/components/web-nav/web-nav';
import { ApiService } from './services/api.service';
import { AuthAppService } from './services/auth-app/auth-app.service';
import { BrowserStorageService } from './services/browser-storage.service';
import { ClarityService } from './services/clarity.service';
import { Constants } from './services/constants';
import { DomHelperService } from './services/dom-helper.service';
import { MaintenanceService } from './services/maintenance.service';
import { StoreService } from './services/store.service';
import { SeoExtentionComponent } from './shared/components/seo-extention/seo-extention';
import { SnackbarComponent } from './shared/components/snackbar/snackbar';
import { CanonicalUrlPipe } from './shared/pipes/canonical-url.pipe';

const googleAuthConfig = Constants.GOOGLE_AUTH_CONFIG;
const intercomConfig = Constants.INTERCOM_CONFIG;

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
  imports: [RouterOutlet, WebFooterComponent, SnackbarComponent, WebNavComponent]
})
export class AppComponent extends SeoExtentionComponent implements OnInit, OnDestroy {
  loggedUser: User;

  readonly showHeader = signal(true);
  readonly showFooter = signal(true);

  isBrowser = false;

  readonly isBusinessDashboard = signal(false);
  readonly isWellbeingDashboard = signal(false);

  constructor(
    private router: Router,
    private authService: AuthAppService,
    private api: ApiService,
    private store: StoreService,
    @Inject(PLATFORM_ID) private platformId: any,
    titleService: Title,
    metaService: Meta,
    @Inject(DOCUMENT) dom_: any,
    canonical_: CanonicalUrlPipe,
    private domHelper: DomHelperService,
    private maintenanceService: MaintenanceService,
    private storage: BrowserStorageService,
    private clarityService: ClarityService
  ) {
    super(router, titleService, metaService, dom_, canonical_);
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  businessId = '';
  userEmail = '';
  hmacHash: any;

  intercomGen = false;

  previousUrl = '';
  currentUrl = '';
  private destroy$ = new Subject<void>();
  private maintenanceStop$ = new Subject<void>();

  ngOnInit(): void {
    this.authService.loggedUser.subscribe({
      next: (user: User) => {
        if (user?.email && (user.is_owner || user.is_practitioner || user.is_group_owner)) {
          this.userEmail = user.email;
          this.businessId = user.businesses[0]?.uuid || '';
          if (!this.intercomGen) {
            this.inItIntercom();
          }
          if (this.isBrowser) {
            this.clarityService.initClarity();
          }
        } else if (!user?.email && this.intercomGen) {
          this.intercomGen = false;
          shutdown();
        }
      }
    });
    super.initialCall();

    this.checkValidJWT();
    if (this.isBrowser) {
      this.handleMaintenanceMode();
      this.getloggedUser();
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          setTimeout(() => {
            const contentContainer: any = document.querySelector('body') || window;
            contentContainer?.scrollIntoView('top');
          }, 300);
          const url_split = event.url.split('/');
          // After redirects, so /home-v3 and /about-avaana land without the global header and footer
          this.showFooter.set(
            !(
              url_split[1] === 'search' ||
              (url_split[1] === 'business' && event.url !== '/business/provider/signup') ||
              url_split[1] === 'wellbeing' ||
              url_split[1] === 'profile' ||
              url_split[1] === 'work-cover' ||
              url_split[1] === 'maintenance'
            )
          );
          this.showHeader.set(!(url_split[1] === 'maintenance'));

          this.isBusinessDashboard.set(url_split[1] === 'business' && event.url !== '/business/provider/signup');
          this.isWellbeingDashboard.set(url_split[1] === 'wellbeing');
        }
      });
      if (googleAuthConfig.API_KEY) {
        this.domHelper.loadScript(
          `https://maps.googleapis.com/maps/api/js?key=${googleAuthConfig.API_KEY}&region=AU&v=weekly&loading=async`
        );
      }
      if (environment.production && !this.authService.isLoggedIn) {
        this.domHelper.loadGtm(Constants.GTM_CONTAINER_ID);
      }
      this.authService.isAuthenticated.pipe(takeUntil(this.destroy$)).subscribe((isAuth) => {
        if (isAuth) {
          this.domHelper.disableGtm();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.maintenanceStop$.next();
    this.maintenanceStop$.complete();
  }

  inItIntercom() {
    const hmacHash = this.store.generateHmac(this.businessId, intercomConfig.secretKey);
    this.intercomGen = true;
    Intercom({
      app_id: intercomConfig.appId,
      user_id: this.businessId,
      email: this.userEmail,
      user_hash: hmacHash
    });
  }

  checkValidJWT() {
    const token = this.authService.getToken();
    if (token) {
      this.api.checkValidSession().subscribe({
        error: () => {
          this.authService.purgeAuth();
        }
      });
    }
  }

  getloggedUser() {
    try {
      const user = this.storage.getItem('localStorage', 'loggedUser');
      if (!user) return;
      this.authService.setloggedUser(JSON.parse(user));
    } catch (error) {
      console.info('cookie read error: IGNORE', error);
    }
  }

  private handleMaintenanceMode() {
    interval(60000)
      .pipe(
        startWith(0),
        switchMap(() => this.maintenanceService.getMaintenanceConfig().pipe(take(1))),
        takeUntil(merge(this.destroy$, this.maintenanceStop$))
      )
      .subscribe((config) => {
        this.store.maintenanceConfig = config;
        if (!config.repeat) {
          this.maintenanceStop$.next();
        }
        const isActive = this.store.isMaintenanceActive(config.maintenance);
        const alreadyOnMaintenance = this.router.url.startsWith('/maintenance');

        if (isActive && !alreadyOnMaintenance) {
          this.router.navigate(['/maintenance']);
        } else if (!isActive && alreadyOnMaintenance) {
          this.router.navigate(['/']);
        }
      });
  }
}
